import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import client, { unwrap } from "@/api/client";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { productImageFor } from "@/lib/stockImages";

export default function ProductDetailPage() {
  const { productId } = useParams();
  const [activeImage, setActiveImage] = useState(0);

  const { data: p } = useQuery({
    queryKey: ["product-detail", productId],
    queryFn: () => client.get(`/catalogues/products/${productId}`).then(unwrap),
  });

  const images = p?.imageUrls?.length ? p.imageUrls : [productImageFor(productId)];

  return (
    <div data-testid="product-detail-page">
      <Link to="/products" className="inline-flex items-center gap-1.5 text-sm text-[#8C8379] hover:text-[#C85A32] transition-colors mb-6">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Products
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-10">
        <div>
          <div className="rounded-xl overflow-hidden border border-[#E6DDD3] aspect-square bg-[#F4EFEA]" data-testid="product-hero-image">
            <img src={images[activeImage]} alt={p?.name} className="h-full w-full object-cover" />
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-4 gap-3 mt-3">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  data-testid={`product-thumbnail-${idx}`}
                  onClick={() => setActiveImage(idx)}
                  className={`aspect-square rounded-lg overflow-hidden border-2 ${activeImage === idx ? "border-[#C85A32]" : "border-[#E6DDD3]"}`}
                >
                  <img src={img} alt={`${p?.name} ${idx + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-[10px] font-semibold tracking-widest text-[#8C8379] uppercase mb-2">{p?.companyName}</p>
          <h1 className="font-serif-display text-3xl text-[#2A211D]" data-testid="product-detail-name">{p?.name}</h1>
          <p className="text-sm text-[#8C8379] mt-1">{p?.versionName || p?.catalogueReference}</p>
          <p className="text-xs font-mono-spec bg-[#F4EFEA] rounded px-2 py-1 mt-4 inline-block" data-testid="product-detail-reference">
            Ref: {p?.shopReference}
          </p>
          <div className="mt-6 space-y-2 text-sm text-[#2A211D]">
            {/* widthPanna is free text that already carries its unit ("54 inch", "137 cm"). */}
            <p>{[p?.fabricType, p?.widthPanna, p?.blackoutLevel].filter(Boolean).join(" · ")}</p>
            <p className="text-[#8C8379] flex items-center gap-2">
              {p?.colourHex && <span className="h-3.5 w-3.5 rounded-full border border-black/10" style={{ backgroundColor: p.colourHex }} />}
              Colour: {p?.colour || p?.colourFamily || "—"}
              {p?.colour && p.colourFamily && p.colourFamily !== p.colour ? ` (${p.colourFamily})` : ""}
              {p?.shadeCode ? ` · Shade ${p.shadeCode}` : ""}
            </p>
            {p?.pattern && <p className="text-[#8C8379]">Pattern: {p.pattern}</p>}
          </div>
          {p?.design && (
            <div className="mt-6 border border-[#E6DDD3] rounded-xl p-4 bg-white" data-testid="product-design-specs">
              <p className="text-[10px] font-semibold tracking-widest text-[#8C8379] uppercase mb-2">
                Design · {p.design.name}{p.design.designNumber ? ` #${p.design.designNumber}` : ""}
              </p>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                {[
                  ["Fabric", p.design.fabricType],
                  ["Composition", p.design.composition],
                  ["Width", p.design.widthCm && `${p.design.widthCm} cm`],
                  ["Weight", p.design.weightGsm && `${p.design.weightGsm} GSM`],
                  ["Martindale", p.design.martindaleRubs && `${p.design.martindaleRubs.toLocaleString("en-IN")} rubs`],
                  ["Pattern", p.design.pattern],
                  ["Usage", p.design.usage?.join(", ")],
                  ["Use", p.design.indoorOutdoor],
                  ["Features", p.design.features?.join(", ")],
                  ["Care", p.design.care],
                ]
                  .filter(([, v]) => v)
                  .map(([k, v]) => (
                    <div key={k} className="contents">
                      <dt className="text-[#8C8379]">{k}</dt>
                      <dd className="text-[#2A211D]">{v}</dd>
                    </div>
                  ))}
              </dl>
            </div>
          )}
          {(p?.sellingPricePerMetre != null || p?.hsnCode) && (
            <p className="mt-4 text-sm text-[#2A211D]">
              {p.sellingPricePerMetre != null && <span className="font-semibold">₹{p.sellingPricePerMetre.toLocaleString("en-IN")} / {p.unit || "metre"}</span>}
              {p.hsnCode && <span className="text-[#8C8379]"> · HSN {p.hsnCode}</span>}
              {p.gstPercent != null && <span className="text-[#8C8379]"> · GST {p.gstPercent}%</span>}
            </p>
          )}
          <Button
            data-testid="add-to-project-button"
            className="mt-8 w-full bg-[#C85A32] hover:bg-[#B04D2A] text-white rounded-full"
            onClick={() => toast("Open a window's workspace to add this fabric to a project")}
          >
            + Add to Project
          </Button>
        </div>
      </div>
    </div>
  );
}
